import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Estado reutilizable de configuración de columnas para tablas de reporte:
 *  - Ocultar/mostrar columnas.
 *  - Reordenar columnas.
 *  - Ordenar filas por una columna (desc → asc → sin orden).
 *
 * Persiste orden + ocultas en localStorage bajo `storageKey` (una clave por
 * reporte, p.ej. `colcfg:productividad:general`). El ordenamiento (`sort`) NO se
 * persiste: es una interacción efímera de la sesión.
 *
 * Reconciliación: si el catálogo de columnas del código cambia (se agrega/quita
 * una columna), el estado persistido se fusiona — se conservan las posiciones
 * conocidas y las columnas nuevas se agregan al final.
 */
export interface ColumnaConfigItem {
  id: string;
  etiqueta: string;
}

export interface SortState {
  id: string;
  dir: 'asc' | 'desc';
}

export interface ColumnasConfig {
  /** ids en orden de despliegue (incluye las ocultas, en su posición). */
  orden: string[];
  /** ids ocultas. */
  ocultas: Set<string>;
  /** ids visibles, ya en orden (orden − ocultas). */
  visibles: string[];
  /** Columna+dirección de ordenamiento activo, o null (orden natural). */
  sort: SortState | null;
  /** Alterna la visibilidad de una columna. */
  toggle: (id: string) => void;
  /** Reordena: coloca `id` en la posición de `destinoId`. */
  mover: (id: string, destinoId: string) => void;
  /** Cicla el orden por esta columna: desc → asc → sin orden. */
  setSort: (id: string) => void;
  /** Restablece orden, visibilidad y ordenamiento a los valores por defecto. */
  reset: () => void;
}

const SIN_OCULTAS: string[] = [];

interface Persistido {
  orden: string[];
  ocultas: string[];
}

/**
 * Lee la configuración persistida y la reconcilia con el catálogo actual:
 * conserva las posiciones conocidas, agrega columnas nuevas al final y descarta
 * ids que ya no existen. Si no hay nada guardado (o falla), devuelve el default.
 *
 * Se usa como inicializador lazy de `useState` — NO como efecto — para evitar la
 * carrera "cargar vs. persistir" (el efecto de persistir del mount inicial, más
 * el doble-montaje de StrictMode, pisaba lo guardado con los valores default).
 */
function cargarInicial(
  storageKey: string,
  idsCatalogo: string[],
  ocultasPorDefecto: string[],
): { orden: string[]; ocultas: Set<string> } {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const p = JSON.parse(raw) as Persistido;
      if (Array.isArray(p.orden)) {
        const existentes = p.orden.filter((id) => idsCatalogo.includes(id));
        const nuevos = idsCatalogo.filter((id) => !existentes.includes(id));
        return {
          orden: [...existentes, ...nuevos],
          ocultas: new Set((p.ocultas ?? []).filter((id) => idsCatalogo.includes(id))),
        };
      }
    }
  } catch {
    // localStorage no disponible o JSON corrupto — se usa el default.
  }
  return { orden: idsCatalogo, ocultas: new Set(ocultasPorDefecto) };
}

export function useColumnasConfig(
  storageKey: string,
  columnas: ColumnaConfigItem[],
  ocultasPorDefecto: string[] = SIN_OCULTAS,
): ColumnasConfig {
  const idsCatalogo = useMemo(() => columnas.map((c) => c.id), [columnas]);

  // Inicialización lazy DESDE localStorage (una sola lectura por estado). El
  // storageKey es fijo por instancia del hook, así que no hace falta un efecto
  // de recarga: leer en el inicializador es suficiente y evita la carrera.
  const [orden, setOrden] = useState<string[]>(
    () => cargarInicial(storageKey, idsCatalogo, ocultasPorDefecto).orden,
  );
  const [ocultas, setOcultas] = useState<Set<string>>(
    () => cargarInicial(storageKey, idsCatalogo, ocultasPorDefecto).ocultas,
  );
  const [sort, setSortState] = useState<SortState | null>(null);

  // Persistir orden + ocultas ante cualquier cambio.
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ orden, ocultas: [...ocultas] }));
    } catch {
      // localStorage no disponible (modo privado, cuota) — se ignora.
    }
  }, [storageKey, orden, ocultas]);

  const toggle = useCallback((id: string) => {
    setOcultas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const mover = useCallback((id: string, destinoId: string) => {
    setOrden((prev) => {
      if (id === destinoId) return prev;
      const arr = prev.filter((x) => x !== id);
      const idx = arr.indexOf(destinoId);
      if (idx < 0) return prev;
      arr.splice(idx, 0, id);
      return arr;
    });
  }, []);

  const setSort = useCallback((id: string) => {
    setSortState((prev) => {
      if (!prev || prev.id !== id) return { id, dir: 'desc' };
      if (prev.dir === 'desc') return { id, dir: 'asc' };
      return null;
    });
  }, []);

  const reset = useCallback(() => {
    setOrden(idsCatalogo);
    setOcultas(new Set(ocultasPorDefecto));
    setSortState(null);
  }, [idsCatalogo, ocultasPorDefecto]);

  const visibles = useMemo(() => orden.filter((id) => !ocultas.has(id)), [orden, ocultas]);

  return { orden, ocultas, visibles, sort, toggle, mover, setSort, reset };
}
